import { useMutation, useQuery } from 'react-query'
import HttpRequest from '@/config/axios.config'
import {
  PriorityStatus,
  TASK_PARAMS_TYPE,
  TASK_PAYLOAD_TYPE,
} from '@/constant/task'
import { v4 as uuidv4 } from 'uuid'
import {
  assign,
  filter,
  get,
  has,
  includes,
  isEmpty,
  isNumber,
  join,
  keys,
  map,
  omit,
  omitBy,
  pick,
  replace,
  sortBy,
  toPairs,
  toString,
  values,
  zipObject,
} from 'lodash'
import moment from 'moment'
import { INSPECTION_TEXT } from '@/screens/QSF/Inspection/Inspection.constant'
import { transformFieldEmptyToNull } from '@/utils/common'
import {
  ControlType,
  REPORT_CONTROL_TYPES,
} from '@/screens/QSF/InspectionReport/InSpectionReport.constant'
import { uploadInspectionTaskPhoto } from '@/screens/QSF/InspectionReport/InSpectionReport.service'
import { RcFile } from 'antd/es/upload'

const {
  SELECT,
  SELECT_DROPDOWN,
  TEXT,
  GROUP,
  NUMBER,
  DATE,
  MULTI_SELECT,
  EDITABLE_ENTRY_LIST,
} = REPORT_CONTROL_TYPES

const useUpdateTaskDetail = () => {
  const api = async (data: any) => {
    const response = await HttpRequest.put(
      `tasks/${data.id}/${data.type}`,
      data.payload
    )
    return response?.data
  }
  return useMutation((data: any) => api(data))
}

const useTaskTable = (params: TASK_PARAMS_TYPE, payload) => {
  const {
    page_number = 1,
    sort = '',
    filter = '',
    page_size = 10,
  }: any = params

  const api = async ({ signal }: any) => {
    try {
      const planned = payload?.planned_date
      const plannedArr = Array.isArray(planned)
        ? planned
        : typeof planned === 'string'
        ? planned.split(',').filter(Boolean)
        : []
      const getPlannedDate = plannedArr.map((dateJs) =>
        moment(dateJs).format(INSPECTION_TEXT.dateFomat)
      )
      const getFilterTile = replace(filter, 'all', '')

      const wrapParams = {
        page_number: page_number.toString(),
        page_size: page_size.toString(),
        filter: getFilterTile,
        sort,
      }
      const toQueryParam = new URLSearchParams(
        omitBy(transformFieldEmptyToNull(wrapParams), isEmpty)
      ).toString()
      const wrapPayload = omitBy(
        {
          ...payload,
          planned_date: join(getPlannedDate, ','),
        },
        isEmpty
      )

      const url = `/tasks/search-advance?${toQueryParam}`
      const response = await HttpRequest.post(url, wrapPayload, {
        signal,
      })

      return response.data
    } catch (error) {
      throw error
    }
  }

  return useQuery({
    queryKey: ['taskData', params, payload],
    queryFn: api,
  })
}

const useTaskActivities = ({ id, enabled }) => {
  const api = async () => {
    try {
      const response = await HttpRequest.get(`tasks/${id}/time-action`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  return useQuery(['taskActivities', id, enabled], api, { enabled })
}

const useTaskSummary = (payload: TASK_PAYLOAD_TYPE = {}) => {
  const transformedData = (data) => {
    try {
      var totalTask = 0
      const summary = data.reduce((acc, { count, priority_status }) => {
        if (priority_status !== PriorityStatus.DONE) {
          totalTask += parseInt(count)
        }
        acc[priority_status] = count
        return acc
      }, {})
      return { ...summary, totalTask }
    } catch (error) {
      return {}
    }
  }
  const api = async () => {
    try {
      const filteredPayload = omitBy(payload, isEmpty)
      const planned = (filteredPayload as any)?.planned_date
      const plannedArr = Array.isArray(planned)
        ? planned
        : typeof planned === 'string'
        ? planned.split(',').filter(Boolean)
        : []
      const getPlannedDate = plannedArr.map((dateJs) =>
        moment(dateJs).format(INSPECTION_TEXT.dateFomat)
      )
      const wrapPayload = omitBy(
        {
          ...filteredPayload,
          planned_date: join(getPlannedDate, ','),
        },
        isEmpty
      )
      const response = await HttpRequest.post(
        `tasks/summary-filter`,
        wrapPayload
      )
      return transformedData(response.data.data)
    } catch (error) {
      throw error
    }
  }
  return useQuery(['taskSummary', payload], api, {
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  })
}
const useOrderCollaborator = ({ id, enabled }) => {
  const transformDate = (data) => {
    return data.map((value) => {
      return {
        ...value,
        label: value.user.first_name + ' ' + value.user.last_name,
        value: value.user.id,
      }
    })
  }
  const api = async () => {
    try {
      const response = await HttpRequest.get(`orders/${id}/users`)
      return transformDate(response.data.data)
    } catch (error) {
      throw error
    }
  }
  return useQuery(['orderCollaborator', id], api, { enabled })
}

const useTaskDocumentCount = ({ id, enabled }) => {
  const api = async () => {
    try {
      const response = await HttpRequest.get(`tasks/${id}/task-document-count`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  return useQuery(['task-document-count', id, enabled], api, { enabled })
}

const getTaskPriorities = async () => {
  try {
    const { data } = await HttpRequest.get('/tasks/priority_statuses')
    return data.data || []
  } catch (error) {}
}

const getTaskStatuses = async () => {
  try {
    const { data } = await HttpRequest.get('/tasks/statuses')
    return data.data || []
  } catch (error) {}
}

const getTaskAssignees = async () => {
  try {
    const { data } = await HttpRequest.get('/tasks/assignees')
    return data.data || []
  } catch (error) {}
}

const getTaskSections = async () => {
  const { data } = await HttpRequest.get('/tasks/sections')
  return data?.data
}

export type EDIT_CHECKPOINT_PAYLOAD_TYPE = {
  value: any
  control_type: ControlType
}

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)

    reader.onerror = (error) => reject(error)
  })

/**
 *
 * @param valueObject the original object from the input form
 * @returns payload submit to the api
 */
const convertObjectValuesOfGroupEntry = (valueObject) => {
  const testKey = keys(valueObject)
  const testVal = values(valueObject)

  const resVal = map(testVal, (item) => {
    if (includes([NUMBER], item.control_type)) {
      const numValue = Number(item.value)

      if (!isNaN(numValue)) {
        item.value = numValue
      }
    }

    return item
  })

  const result = zipObject(testKey, resVal)

  return result
}

const useUpdateCheckpoint = () => {
  const requestFN = async ({ id, payload }) => {
    try {
      const {
        value,
        control_type,
        entryIndex = null,
        checkpointName = '',
      } = payload
      const wrapPayloadByTypeFN = async (v) => {
        if (includes([NUMBER], control_type)) {
          const result = values(v)[0]

          return isNumber(result) ? result : null
        }

        if (includes([TEXT], control_type)) {
          const result = values(v)[0]

          return result
        }

        if (includes([EDITABLE_ENTRY_LIST], control_type)) {
          const result = filter(values(v)[0], Boolean)

          return result
        }

        if (includes([DATE], control_type)) {
          return values(v)[0]
        }

        if (includes([MULTI_SELECT], control_type)) {
          return values(v)[0]
        }

        if (includes([SELECT, SELECT_DROPDOWN], control_type)) {
          return values(v)[0] || { label: '', value: '' }
        }

        if (includes([GROUP], control_type)) {
          const covertObj = values(v)[0]
          const getKeyPhotoExtra: any = get(covertObj, 'photoExtraOption')

          if (getKeyPhotoExtra) {
            const isNewImage = has(getKeyPhotoExtra, 'originFileObj')

            const pickField = pick(getKeyPhotoExtra, [
              'originalId',
              'thumbnailId',
              'comment',
              'checkpointName',
              'photoIndex',
            ])

            if (!isNewImage) {
              const prePayloadPhoto = {
                ...pickField,
                type: 'CHECKPOINT',
                getKeyPhotoExtra,
              }

              return prePayloadPhoto
            } else {
              const {
                originFileObj,
                type,
                size,
                thumbUrl,
                comment,
                photoIndex,
                originalId,
              } = getKeyPhotoExtra

              const data_raw = await getBase64(originFileObj)
              const imageName = uuidv4()
              const thumbUUID = uuidv4()

              const payload = {
                media_id: imageName,
                table: 'inspections',
                name: imageName,
                data_raw: data_raw,
                type: type,
                size: `${size}`,
              }
              const payloadThumbnail = {
                media_id: thumbUUID,
                table: 'inspections',
                name: `thumbnail-${thumbUUID}`,
                data_raw: thumbUrl,
                type: type,
                size: `${size}`,
              }

              const [orgImageData, thumbImageData] = await Promise.all([
                uploadInspectionTaskPhoto(payload),
                uploadInspectionTaskPhoto(payloadThumbnail),
              ])

              const prePayloadPhoto = {
                ...pickField,
                mediaId: orgImageData.id,
                originalId,
                thumbnailId: thumbImageData.id,
                comment: comment,
                type: 'CHECKPOINT',
                key: toString(photoIndex),
                getKeyPhotoExtra,
              }

              if (!originalId) {
                prePayloadPhoto.originalId = orgImageData.id
                prePayloadPhoto.mediaId = null
              }

              return prePayloadPhoto
            }
          } else {
            convertObjectValuesOfGroupEntry(values(v)[0])
            return values(v)[0]
          }
        }
      }
      const getValue = await wrapPayloadByTypeFN(value)
      if (includes([GROUP], control_type)) {
        // const { checkpointName } = getValue
        if (has(getValue, 'getKeyPhotoExtra')) {
          const { getKeyPhotoExtra } = getValue
          const photoCheckpointName = get(getKeyPhotoExtra, 'checkpointName')
          if (getKeyPhotoExtra.isUpdate) {
            return await HttpRequest.patch(
              `tasks/${id}/comment?reference_name=${photoCheckpointName}&index=${entryIndex}`,
              getValue
            )
          } else {
            return await HttpRequest.post(
              `tasks/${id}/add-new-photo-comment?reference_name=${photoCheckpointName}&index=${entryIndex}`,
              getValue
            )
          }
        }
        // Mobile pass with checkpoint_id, unfortunately api not support yet
        const omitPrepareValue = omit(getValue, ['checkpoint_id'])
        const mapPayload = {
          value: omitPrepareValue,
        }

        const response = await HttpRequest.patch(
          `tasks/${id}/edit?reference_name=${checkpointName}&index=${entryIndex}`,
          mapPayload
        )

        return response
      } else {
        const mapPayload = {
          value: getValue,
        }

        return await HttpRequest.patch(`tasks/${id}/update`, mapPayload)
      }
    } catch (error) {
      throw error
    }
  }

  const result = useMutation(async (payload: any) => await requestFN(payload))

  return result
}

//TODO validate when open the page
const getTaskGroupEntry = (subSectionId: string, checkpointId: number) => {
  const api = async () => {
    try {
      const response = await HttpRequest.get(
        `tasks/${subSectionId}/${checkpointId}/edit`
      )
      const convertToPare = toPairs(response.data.data)
      const sortByIndex = sortBy(convertToPare, ([, v]: any) => v.index)
      const revertBackToObj = map(sortByIndex, ([key, value]) => ({
        [key]: value,
      }))
      const assignObject = assign({}, ...revertBackToObj)

      return assignObject
    } catch (error) {
      throw error
    }
  }

  return useQuery({
    queryKey: ['GET_TASK_AS_GROUP_ENTRY', subSectionId, checkpointId],
    queryFn: async () => await api(),
    enabled: !!subSectionId,
  })
}

const useDeleteGroupPhoto = () => {
  const requestFN = async ({
    subSectionId,
    originalId,
    checkpointName,
    entryIndex,
  }) => {
    return await HttpRequest.delete(
      `tasks/${subSectionId}/comment/${originalId}?reference_name=${checkpointName}&index=${entryIndex}`
    )
  }

  const result = useMutation(async (payload: any) => {
    return await requestFN(payload)
  })

  return result
}

export {
  useTaskTable,
  useTaskActivities,
  useUpdateTaskDetail,
  useTaskSummary,
  useOrderCollaborator,
  useTaskDocumentCount,
  getTaskPriorities,
  getTaskStatuses,
  getTaskAssignees,
  getTaskSections,
  useUpdateCheckpoint,
  getTaskGroupEntry,
  useDeleteGroupPhoto,
}
